import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, MapPin, Phone } from 'lucide-react';
import SITE_CONFIG from '../config/siteConfig';

const ContactPage = () => {
  return (
    <>
      <Helmet>
        <title>Contact Us - AI Carousel Maker</title>
        <link rel="canonical" href={`${SITE_CONFIG.siteUrl}/contact`} />
        <meta name="description" content="Get in touch with AI Carousel Maker. Contact us for support, questions, or feedback about our AI-powered carousel generation platform." />
        <meta property="og:title" content="Contact Us - AI Carousel Maker" />
        <meta property="og:url" content={`${SITE_CONFIG.siteUrl}/contact`} />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-600">
            Have questions? We'd love to hear from you.
          </p>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          <div className="space-y-8">
            {/* Email */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Email Us
                </h3>
                <p className="text-gray-600 mb-2">
                  Send us an email and we'll get back to you as soon as possible.
                </p>
                <a 
                  href="mailto:support@novialtechnologies.com"
                  className="text-purple-600 hover:text-purple-700 font-medium text-lg"
                >
                  support@novialtechnologies.com
                </a>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200"></div>

            {/* Business Hours */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Phone className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Response Time
                </h3>
                <p className="text-gray-600">
                  We typically respond within 24-48 hours during business days.
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Monday - Friday: 9:00 AM - 6:00 PM IST
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200"></div>

            {/* Location */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-pink-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Location
                </h3>
                <p className="text-gray-600">
                  Novial Technologies
                </p>
                <p className="text-gray-500 text-sm mt-1">
                  Ahmedabad, India
                </p>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-12 p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              📧 What to Include in Your Email
            </h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">•</span>
                <span>Your account email (if applicable)</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">•</span>
                <span>A detailed description of your question or issue</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">•</span>
                <span>Screenshots (if relevant)</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">•</span>
                <span>Any error messages you received</span>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              Looking for something specific?
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="/pricing" 
                className="px-6 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors font-medium"
              >
                View Pricing
              </a>
              <a 
                href="/terms-and-conditions" 
                className="px-6 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium"
              >
                Terms & Conditions
              </a>
              <a 
                href="/privacy-policy" 
                className="px-6 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition-colors font-medium"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ContactPage;
